'use client'
import { type FC, useEffect, useMemo } from 'react'
import { Button, Checkbox, HStack, SimpleGrid, VStack } from '@chakra-ui/react'
import { z } from 'zod'
import {
  FormProvider,
  GrandFormCountrySelect,
  GrandFormInputField,
  GrandFormSubmitButton,
  type Resolver,
  useForm,
  zodResolver
} from 'forms/grand-form'
import type { CountryCode, OrderAddressDTO } from 'ecosystem'

/**
 * Build the validation schema.
 *
 * Messages are passed in rather than left to zod, whose defaults surface raw internals
 * ("Too small: expected string to have >=1 characters") straight to the shopper, in
 * English regardless of the store's language.
 *
 * When `requireContactDetails` is set the contact fields become mandatory: a Briqpay B2C
 * session is payment-only, so without them a carrier booking goes out with an empty party
 * and is rejected. The name is asked for as two fields rather than one because Briqpay's
 * `data.billing` takes `firstName` and `lastName` separately, and splitting a single
 * string guesses wrong on multi-part surnames.
 */
/**
 * Deliberately permissive: 6–15 digits after stripping spaces, hyphens, dots, brackets
 * and a leading `+`. That is the E.164 range, so it rejects the realistic mistakes — a
 * postcode typed into the phone field, a half-entered number, stray letters — without
 * pretending to know each country's numbering plan. Carrier bookings and SMS
 * notifications only need it to be plausibly a phone number.
 */
const PHONE_PATTERN = /^\+?[\d\s\-.()]{6,}$/
const isPlausiblePhone = (value: string) => {
  if (!PHONE_PATTERN.test(value)) return false
  const digits = value.replace(/\D/g, '')
  return digits.length >= 6 && digits.length <= 15
}

const buildSchema = ({
  requireContactDetails,
  requiredError,
  emailError,
  phoneError
}: {
  requireContactDetails: boolean
  requiredError: string
  emailError: string
  phoneError: string
}) => {
  const required = z.string().min(1, { message: requiredError })
  const optionalString = z.string().optional().default('')

  return z.object({
    address1: required,
    address2: optionalString,
    postalCode: required,
    postalRegion: required,
    country: required,
    reference: optionalString,
    attention: optionalString,
    firstName: requireContactDetails ? required : optionalString,
    lastName: requireContactDetails ? required : optionalString,
    mobilePhone: requireContactDetails
      ? required.refine(isPlausiblePhone, { message: phoneError })
      : // Still validate a volunteered number, but never block on an empty one.
        optionalString.refine((value) => !value || isPlausiblePhone(value), {
          message: phoneError
        }),
    email: requireContactDetails ? required.email({ message: emailError }) : optionalString
  })
}

type CustomAddressFormValues = z.infer<ReturnType<typeof buildSchema>>

/**
 * An order address plus the contact details that have no slot on `OrderAddressDTO`.
 * Populated only when the form was asked for contact details.
 */
export interface OrderAddressWithContact extends OrderAddressDTO {
  firstName?: string
  lastName?: string
  email?: string
}

const toOrderAddress = (values: CustomAddressFormValues): OrderAddressWithContact => ({
  // `attention` stays the canonical single-string contact person — it is what
  // `OrderAddressDTO` carries and what the carrier bookings send as `contactName`.
  attention:
    `${values.firstName ?? ''} ${values.lastName ?? ''}`.trim() || (values.attention ?? ''),
  mobilePhone: values.mobilePhone ?? '',
  address1: values.address1,
  address2: values.address2 ?? '',
  postalCode: values.postalCode,
  postalRegion: values.postalRegion,
  country: values.country,
  reference: values.reference ?? '',
  ...(values.firstName ? { firstName: values.firstName } : {}),
  ...(values.lastName ? { lastName: values.lastName } : {}),
  ...(values.email ? { email: values.email } : {})
})

const toFormValues = (
  defaultValues: OrderAddressWithContact,
  defaultCountry?: CountryCode
): CustomAddressFormValues => ({
  attention: defaultValues.attention ?? '',
  mobilePhone: defaultValues.mobilePhone ?? '',
  address1: defaultValues.address1 ?? '',
  address2: defaultValues.address2 ?? '',
  postalCode: defaultValues.postalCode ?? '',
  postalRegion: defaultValues.postalRegion ?? '',
  country: defaultValues.country || (defaultCountry ?? ''),
  reference: defaultValues.reference ?? '',
  firstName: defaultValues.firstName ?? '',
  lastName: defaultValues.lastName ?? '',
  email: defaultValues.email ?? ''
})

export interface CustomAddressFormLabels {
  /** Shown under any required field the shopper left blank. */
  requiredError: string
  /** Shown when the email field is filled but not a valid address. */
  emailError: string
  phoneError: string
  attention: string
  firstName: string
  lastName: string
  email: string
  mobilePhone: string
  address1: string
  address2: string
  postalCode: string
  postalRegion: string
  country: string
  reference: string
  cancel: string
  save: string
  /** Label on the "remember my details on this device" checkbox. */
  rememberOnDevice: string
}

interface CustomAddressFormProps {
  defaultValues: OrderAddressWithContact
  labels: CustomAddressFormLabels
  onCancel: () => void
  onSubmit: (next: OrderAddressWithContact) => void
  /**
   * Render the free-text reference field. Default on.
   *
   * It exists for the B2B invoice flow, where it becomes the purchase reference printed
   * on the invoice. Flows with nothing to print it on should turn it off rather than ask
   * the shopper for something no downstream system reads.
   */
  showReference?: boolean
  /**
   * Render the phone field. Default on.
   *
   * Turn it off where nothing downstream reads it — a delivery address for a flow that
   * books no carrier, when the payer's phone is already collected elsewhere. Asking
   * twice for the same number is just friction.
   */
  showPhone?: boolean
  /**
   * Preselects the country when the shopper has no saved address.
   *
   * The dropdown itself is deliberately unfiltered: this is a *billing* address, which is
   * independent of the session country the payment provider was created with — a shopper
   * checking out on the Swedish market may well be billed in Italy.
   */
  defaultCountry?: CountryCode
  /**
   * Require a contact person, phone and email, and render the email field.
   *
   * Off by default: most flows get the shopper's identity from the payment widget
   * (Klarna, Svea, Qliro, Nexi) or from the signed-in company account (B2B invoice), so
   * asking again is duplicate work. Turn it on where nothing else collects them.
   */
  requireContactDetails?: boolean
  /**
   * Current state of the "remember my details on this device" checkbox, and the handler
   * that records a change.
   *
   * The box is the shopper's explicit request to have their details kept on this device
   * — the thing that makes storing them lawful at all — so it is rendered only when the
   * parent actually wires it up, never as decoration.
   */
  rememberOnDevice?: { checked: boolean; onChange: (next: boolean) => void }
}

/**
 * `forms/grand-form` powered manual-address form used inside
 * `CompanyAddressPicker` when the shopper picks the "custom address"
 * option. Validates required address fields with zod and emits a typed
 * `OrderAddressDTO` to the parent on submit.
 *
 * Address-shape only — neutral with regards to billing vs shipping; the
 * parent picker supplies the labels for the surrounding context.
 */
export const CustomAddressForm: FC<CustomAddressFormProps> = ({
  defaultValues,
  labels,
  onCancel,
  onSubmit,
  defaultCountry,
  showReference = true,
  showPhone = true,
  requireContactDetails = false,
  rememberOnDevice
}) => {
  const schema = useMemo(
    () =>
      buildSchema({
        requireContactDetails,
        requiredError: labels.requiredError,
        emailError: labels.emailError,
        phoneError: labels.phoneError
      }),
    [requireContactDetails, labels.requiredError, labels.emailError, labels.phoneError]
  )

  const formValues = useMemo(
    () => toFormValues(defaultValues, defaultCountry),
    [defaultValues, defaultCountry]
  )

  const form = useForm<CustomAddressFormValues>({
    resolver: zodResolver(schema) as Resolver<CustomAddressFormValues>,
    mode: 'onTouched',
    defaultValues: formValues
  })

  // `defaultValues` can land after first paint — a device-remembered address only
  // resolves once the signed-in account has settled, by which point `useForm` has
  // already taken its defaults. Apply the late arrival while the form is still
  // untouched; the moment the shopper types, what they typed wins.
  useEffect(() => {
    if (form.formState.isDirty) return
    form.reset(formValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `form` is stable; re-running on it would loop
  }, [formValues])

  return (
    <FormProvider {...form}>
      <VStack
        align="stretch"
        as="form"
        gap={3}
        onSubmit={form.handleSubmit((values) => onSubmit(toOrderAddress(values)))}
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
          <GrandFormInputField
            label={labels.postalCode}
            name="postalCode"
            placeholder={labels.postalCode}
            required
          />
          <GrandFormInputField
            label={labels.postalRegion}
            name="postalRegion"
            placeholder={labels.postalRegion}
            required
          />
          <GrandFormCountrySelect
            label={labels.country}
            name="country"
            placeholder={labels.country}
            required
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
          <GrandFormInputField
            label={labels.address1}
            name="address1"
            placeholder={labels.address1}
            required
          />
          <GrandFormInputField
            label={labels.address2}
            name="address2"
            optional
            placeholder={labels.address2}
          />
        </SimpleGrid>

        {requireContactDetails ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            <GrandFormInputField
              label={labels.firstName}
              name="firstName"
              placeholder={labels.firstName}
              required
            />
            <GrandFormInputField
              label={labels.lastName}
              name="lastName"
              placeholder={labels.lastName}
              required
            />
          </SimpleGrid>
        ) : null}

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
          {requireContactDetails ? (
            <GrandFormInputField
              label={labels.email}
              name="email"
              placeholder={labels.email}
              required
              type="email"
            />
          ) : (
            <GrandFormInputField
              label={labels.attention}
              name="attention"
              optional
              placeholder={labels.attention}
            />
          )}
          {showPhone ? (
            <GrandFormInputField
              label={labels.mobilePhone}
              name="mobilePhone"
              optional={!requireContactDetails}
              placeholder={labels.mobilePhone}
              required={requireContactDetails}
            />
          ) : null}
        </SimpleGrid>

        {showReference ? (
          <GrandFormInputField
            label={labels.reference}
            name="reference"
            optional
            placeholder={labels.reference}
          />
        ) : null}
        {rememberOnDevice ? (
          <Checkbox
            borderColor="primary"
            isChecked={rememberOnDevice.checked}
            onChange={(event) => rememberOnDevice.onChange(event.target.checked)}
            size="md"
          >
            {labels.rememberOnDevice}
          </Checkbox>
        ) : null}

        <HStack gap={2} justify="flex-end">
          <Button onClick={onCancel} size="sm" variant="ghost">
            {labels.cancel}
          </Button>
          <GrandFormSubmitButton size="sm">{labels.save}</GrandFormSubmitButton>
        </HStack>
      </VStack>
    </FormProvider>
  )
}
